import { useState, useEffect } from "react";
import type { LeadSource, LeadStage, Priority, Industry, ScoreCategory } from "@/mocks/leadsData";
import { allSources, allStages, allPriorities, allIndustries, allAgents } from "@/mocks/leadsData";
import { fetchApi } from "@/services/apiService";

type Props = {
  search: string;
  onSearchChange: (v: string) => void;
  sourceFilter: LeadSource | "all";
  onSourceFilterChange: (v: LeadSource | "all") => void;
  stageFilter: LeadStage | "all";
  onStageFilterChange: (v: LeadStage | "all") => void;
  assignedFilter: string | "all";
  onAssignedFilterChange: (v: string | "all") => void;
  industryFilter: Industry | "all";
  onIndustryFilterChange: (v: Industry | "all") => void;
  priorityFilter: Priority | "all";
  onPriorityFilterChange: (v: Priority | "all") => void;
  dateFilter: string;
  onDateFilterChange: (v: string) => void;
  scoreFilter: ScoreCategory;
  onScoreFilterChange: (v: ScoreCategory) => void;
  onClearFilters: () => void;
  staleFilterActive: boolean;
  onToggleStaleFilter: () => void;
};

const dateOptions = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
];

const scoreOptions: { value: ScoreCategory; label: string }[] = [
  { value: "all", label: "All Scores" },
  { value: "hot", label: "Hot (71-100)" },
  { value: "warm", label: "Warm (41-70)" },
  { value: "cold", label: "Cold (0-40)" },
];

export default function FilterBar({
  search,
  onSearchChange,
  sourceFilter,
  onSourceFilterChange,
  stageFilter,
  onStageFilterChange,
  assignedFilter,
  onAssignedFilterChange,
  industryFilter,
  onIndustryFilterChange,
  priorityFilter,
  onPriorityFilterChange,
  dateFilter,
  onDateFilterChange,
  scoreFilter,
  onScoreFilterChange,
  onClearFilters,
  staleFilterActive,
  onToggleStaleFilter,
}: Props) {
  const [dbStages, setDbStages] = useState<string[]>([]);
  const [dbSources, setDbSources] = useState<string[]>([]);
  const [dbAgents, setDbAgents] = useState<string[]>([]);
  const [agentLeadCounts, setAgentLeadCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchApi<any[]>('/config/pipeline-stages/get-all', undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          const names = items.map((s: any) => s.name).filter(Boolean);
          if (names.length > 0) setDbStages(names);
        }
      })
      .catch(() => {});

    fetchApi<any[]>('/config/lead-sources/get-all', undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          const names = items.map((s: any) => s.name).filter(Boolean);
          if (names.length > 0) setDbSources(names);
        }
      })
      .catch(() => {});

    fetchApi<any>('/auth/company-user/all', undefined, null)
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const agentNames: string[] = [];
        userList.forEach((t: any) => {
          if (t.name) agentNames.push(t.name);
          else if (t.fullName) agentNames.push(t.fullName);
          else if (t.username) agentNames.push(t.username);
          if (t.leader_name) agentNames.push(t.leader_name);
          if (Array.isArray(t.member_names)) agentNames.push(...t.member_names);
        });
        const unique = Array.from(new Set(agentNames)).filter(Boolean);
        if (unique.length > 0) setDbAgents(unique);
      })
      .catch(() => {});

    fetchApi<any>('/leads/stats', undefined, null)
      .then((res) => {
        const counts = res?.agentLeadCounts || res?.data?.agentLeadCounts;
        if (counts && typeof counts === 'object') {
          setAgentLeadCounts(counts);
        }
      })
      .catch(() => {});
  }, []);

  const stageOptions = dbStages.length > 0 ? dbStages : allStages;
  const sourceOptions = dbSources.length > 0 ? dbSources : allSources;
  const agentOptions = dbAgents.length > 0 ? dbAgents : allAgents;

  const hasActiveFilters =
    sourceFilter !== "all" ||
    stageFilter !== "all" ||
    assignedFilter !== "all" ||
    industryFilter !== "all" ||
    priorityFilter !== "all" ||
    dateFilter !== "all" ||
    scoreFilter !== "all";

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5 space-y-3">
      {/* Row 1 — Search + primary filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by name, email, phone, company..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
          />
        </div>

        <select
          value={sourceFilter}
          onChange={(e) => onSourceFilterChange(e.target.value as LeadSource | "all")}
          className="px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all whitespace-nowrap"
        >
          <option value="all">All Sources</option>
          {sourceOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={stageFilter}
          onChange={(e) => onStageFilterChange(e.target.value as LeadStage | "all")}
          className="px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all whitespace-nowrap"
        >
          <option value="all">All Stages</option>
          {stageOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={assignedFilter}
          onChange={(e) => onAssignedFilterChange(e.target.value)}
          className="px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all whitespace-nowrap"
        >
          <option value="all">All Agents</option>
          {agentOptions.map((a) => {
            const count = agentLeadCounts[a];
            const countText = count !== undefined ? ` (${count})` : "";
            return (
              <option key={a} value={a}>
                {a}{countText}
              </option>
            );
          })}
        </select>
      </div>

      {/* Row 2 — secondary filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <select
          value={industryFilter}
          onChange={(e) => onIndustryFilterChange(e.target.value as Industry | "all")}
          className="px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all whitespace-nowrap"
        >
          <option value="all">All Industries</option>
          {allIndustries.map((i) => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>

        <select
          value={priorityFilter}
          onChange={(e) => onPriorityFilterChange(e.target.value as Priority | "all")}
          className="px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all whitespace-nowrap"
        >
          <option value="all">All Priority</option>
          {allPriorities.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>

        <select
          value={scoreFilter}
          onChange={(e) => onScoreFilterChange(e.target.value as ScoreCategory)}
          className="px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all whitespace-nowrap"
        >
          {scoreOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          value={dateFilter}
          onChange={(e) => onDateFilterChange(e.target.value)}
          className="px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all whitespace-nowrap"
        >
          {dateOptions.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>

        {/* Stale Leads quick filter chip */}
        <button
          onClick={onToggleStaleFilter}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium cursor-pointer transition-all duration-200 whitespace-nowrap ${
            staleFilterActive
              ? "bg-red-600 text-white border border-red-600"
              : "bg-white text-red-600 border border-red-300 hover:bg-red-50"
          }`}
        >
          <i className={`${staleFilterActive ? "ri-close-line" : "ri-time-line"} text-sm`} />
          Stale Leads
          {staleFilterActive && " ×"}
        </button>

        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-close-line" /> Clear Filters
          </button>
        )}
      </div>
    </div>
  );
}